import { useLayoutEffect, useMemo } from "react";
import { type Atom, useCreateAtom } from "@tanstack/react-store";
import type {
    DataGridActiveEditor,
    DataGridAtoms,
    DataGridCellAddress,
    DataGridColumnState,
    DataGridRangeAction,
} from "./types";

const NO_HIGHLIGHTS: readonly DataGridCellAddress[] = [];
const NO_VARIANTS: Record<string, string> = {};

/** The `useDataGrid` options the atoms start from or follow. */
interface GridAtomOptions {
    columnState?: Partial<DataGridColumnState>;
    defaultColumnState?: Partial<DataGridColumnState>;
    highlightedCells?: readonly DataGridCellAddress[];
}

/**
 * The table-owned atoms outside TanStack state, created once per table.
 * `highlights` follows `highlightedCells`; `columnVariants` starts from
 * `columnState` (or `defaultColumnState`) and then follows `useColumnStateSync`.
 */
export const useGridAtoms = ({
    allMatchingSelected,
    options,
}: {
    allMatchingSelected: Atom<boolean>;
    options: GridAtomOptions;
}): DataGridAtoms => {
    const activeEditor = useCreateAtom<DataGridActiveEditor | null>(null);
    const highlights =
        useCreateAtom<readonly DataGridCellAddress[]>(NO_HIGHLIGHTS);
    const activeRowKey = useCreateAtom<string | null>(null);
    const rangeAction = useCreateAtom<DataGridRangeAction | null>(null);
    const listCellFocus = useCreateAtom(false);
    const columnVariants = useCreateAtom(
        (options.columnState ?? options.defaultColumnState)?.variants ??
            NO_VARIANTS,
    );
    const highlightedCells = options.highlightedCells ?? NO_HIGHLIGHTS;
    useLayoutEffect(() => {
        highlights.set(highlightedCells);
    }, [highlights, highlightedCells]);
    return useMemo(
        () => ({
            activeEditor,
            highlights,
            activeRowKey,
            allMatchingSelected,
            rangeAction,
            listCellFocus,
            columnVariants,
        }),
        [
            activeEditor,
            highlights,
            activeRowKey,
            allMatchingSelected,
            rangeAction,
            listCellFocus,
            columnVariants,
        ],
    );
};
